/**
 * Zentrale Preiskonfiguration für SkyVision CIRCULAR
 */

// Grundkonfiguration für SkyVision CIRCULAR
export const SKYVISION_CIRCULAR_CONFIG = {
  basePrice: 3941, // Grundpreis in Euro (600mm, 2-fach, Standardhöhe 240mm)
  baseDiameter: 600, // Standard-Durchmesser in mm
  stepSize: 100, // Schrittgröße in mm (100mm Schritte)
  standardHeight: 240, // Standardhöhe in mm für 2-fach Verglasung
  
  priceRules: {
    limits: {
      minDiameter: 600,
      maxDiameter: 2000
    }
  },
  
  // Preistabelle für 2-fach Verglasung (Durchmesser in mm => Preis in €)
  // Standardhöhe 240mm
  priceTable2fach: {
    600: 3941,
    700: 4080,
    800: 4227,
    900: 4383,
    1000: 4639,
    1100: 5074,
    1200: 5271,
    1300: 5647,
    1400: 5864,
    1500: 6091,
    1600: 6822,
    1700: 7068,
    1800: 7325,
    1900: 8345,
    2000: 11454
  },
  
  // Preistabelle für 3-fach Verglasung (Durchmesser in mm => Preis in €)
  // Standardhöhe 240mm
  priceTable3fach: {
    600: 4138,
    700: 4319,
    800: 4513,
    900: 4720,
    1000: 5110,
    1100: 5617,
    1200: 5892,
    1300: 6349,
    1400: 6653,
    1500: 6972,
    1600: 7800,
    1700: 8149,
    1800: 8512,
    1900: 9880,
    2000: 13272
  },
  
  glassTypes: {
    double: {
      name: '2-fach Isolierverglasung',
      description: 'Sehr gute Wärmedämmung (Ug-Wert 1,1)',
      minHeight: 240
    },
    triple: {
      name: '3-fach Isolierverglasung', 
      description: 'Maximale Energieeffizienz (Ug-Wert 0,7)',
      minHeight: 240
    }
  }
};

/**
 * Glasoptionen / Accessories Konfiguration für CIRCULAR
 * Preise werden auf Glasfläche berechnet (Kreisfläche)
 * 
 * Hinweis: Nur Sonnenschutzglas verfügbar (Opallaminierung nicht für runde Fenster)
 */
export const CIRCULAR_GLASS_OPTIONS = {
  sun_protection_glass: {
    id: 'sun_protection_glass',
    accessory_code: 'sun_protection_glass',
    name: 'Sonnenschutzglas',
    description: 'Reduziert die Sonneneinstrahlung und Wärmeentwicklung (nur bei 3-fach Verglasung)',
    price_type: 'per_sqm' as const,
    base_price: 135, // Fallback; live kommt der m²-Preis aus dem Zubehörkatalog
    pricePerSqm: 135,
    minPrice: 135,
    glass_type_restriction: 'triple' as const, // Nur bei 3-fach
    glassTypeRestriction: 'triple' as const,
    calculateArea: (diameter: number) => {
      // Glasfläche = Kreisfläche mit Durchmesser + 260mm (130mm Überstand je Seite)
      const glassDiameter = (diameter + 260) / 1000; // in Meter
      const radius = glassDiameter / 2;
      return Math.PI * radius * radius; // πr²
    }
  }
  // Opallaminierung ist für CIRCULAR nicht verfügbar (nicht für runde Fenster geeignet)
};

/**
 * Zusätzliche Zubehör-Optionen für CIRCULAR
 * Diese sind nicht glasflächenabhängig
 */
export const CIRCULAR_ACCESSORIES = {
  rain_sensor: {
    id: 'rain_sensor',
    accessory_code: 'rain_sensor',
    name: 'Regensensor',
    description: 'Schließt das Fenster automatisch bei Regen (optional)',
    price_type: 'fixed' as const,
    base_price: 150,
    glass_type_restriction: 'both' as const,
    category: 'smart_home'
  },
  smoke_detector: {
    id: 'smoke_detector',
    accessory_code: 'smoke_detector',
    name: 'Rauchmelder',
    description: 'Rauchmelder mit Alarmfunktion (optional)',
    price_type: 'fixed' as const,
    base_price: 89,
    glass_type_restriction: 'both' as const,
    category: 'smart_home'
  },
  installation_service: {
    id: 'installation_service',
    accessory_code: 'installation_service',
    name: 'Montage-Service',
    description: 'Professionelle Montage durch Fachpersonal',
    price_type: 'fixed' as const,
    base_price: 450,
    glass_type_restriction: 'both' as const,
    category: 'services'
  }
};

/**
 * Alle verfügbaren Zubehör-Optionen für CIRCULAR (kombiniert)
 */
export const CIRCULAR_ALL_ACCESSORIES = {
  ...CIRCULAR_GLASS_OPTIONS,
  ...CIRCULAR_ACCESSORIES
};

/**
 * Berechnet den Preis für ein Glasoption/Accessory bei CIRCULAR
 */
export const calculateCircularAccessoryPrice = (
  accessoryId: keyof typeof CIRCULAR_ALL_ACCESSORIES,
  diameter?: number
): number => {
  const option = CIRCULAR_ALL_ACCESSORIES[accessoryId];
  if (!option) return 0;
  
  // Für flächenabhängige Optionen (Glasoptionen)
  if (option.price_type === 'per_sqm' && diameter && 'calculateArea' in option) {
    const area = option.calculateArea(diameter);
    const calculatedPrice = option.pricePerSqm * area;
    
    // Minimum-Preis wenn unter 1m²
    return area < 1.0 ? option.minPrice : Math.round(calculatedPrice);
  }
  
  // Für feste Preise (Services, Smart Home, etc.)
  if (option.price_type === 'fixed') {
    return option.base_price;
  }
  
  return 0;
};

/**
 * Rundet Durchmesser auf 100mm-Schritte auf
 */
const roundToStep = (value: number, step: number = 100): number => {
  return Math.ceil(value / step) * step;
};

/**
 * Berechnet den Preis für SkyVision CIRCULAR basierend auf der Preistabelle
 */
export const calculateCircularPrice = (
  diameter: number,
  glassType: 'double' | 'triple' = 'double'
): number => {
  const config = SKYVISION_CIRCULAR_CONFIG;
  
  // Runde auf 100mm Schritte auf
  const roundedDiameter = roundToStep(diameter, 100);
  
  // Hole Preis aus entsprechender Tabelle
  const priceTable = glassType === 'triple' ? config.priceTable3fach : config.priceTable2fach;
  const price = priceTable[roundedDiameter as keyof typeof priceTable];
  
  if (!price) {
    console.warn(`Kein Preis gefunden für Durchmesser ${roundedDiameter}mm (${glassType})`);
    return 0;
  }
  
  return Math.round(price);
};

// Legacy-Funktion entfernt - verwende jetzt calculateCircularPrice mit 10mm-Schritten

/**
 * Prüft ob der Durchmesser gültig ist
 */
export const validateCircularDimensions = (
  diameter: number
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const config = SKYVISION_CIRCULAR_CONFIG;
  
  if (diameter < config.priceRules.limits.minDiameter) {
    errors.push(`Mindestdurchmesser: ${config.priceRules.limits.minDiameter}mm`);
  }
  
  if (diameter > config.priceRules.limits.maxDiameter) {
    errors.push(`Maximaldurchmesser: ${config.priceRules.limits.maxDiameter}mm`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Gibt eine detaillierte Preisaufschlüsselung zurück (Tabellenbasiert)
 */
export const getCircularPriceBreakdown = (
  diameter: number,
  glassType: 'double' | 'triple' = 'double'
): {
  basePrice: number;
  totalPrice: number;
  breakdown: string[];
  roundedDiameter: number;
} => {
  const config = SKYVISION_CIRCULAR_CONFIG;
  const roundedDiameter = roundToStep(diameter, 100);
  
  const priceTable = glassType === 'triple' ? config.priceTable3fach : config.priceTable2fach;
  const totalPrice = priceTable[roundedDiameter as keyof typeof priceTable] || 0;

  const breakdown = [
    `Grundpreis ${glassType === 'triple' ? '3-fach' : '2-fach'} (Ø${roundedDiameter}mm): ${totalPrice}€`
  ];

  return {
    basePrice: totalPrice,
    totalPrice,
    breakdown,
    roundedDiameter
  };
};

// Legacy-Funktionen entfernt - verwende jetzt calculateCircularPrice und getCircularPriceBreakdown

/**
 * Hilfsfunktionen für erweiterte Preiskonfiguration
 */

/**
 * Aktualisiert den Grundpreis (automatische Neuberechnung der Preisstufen)
 */
export const updateCircularBasePrice = (newPrice: number): void => {
  SKYVISION_CIRCULAR_CONFIG.basePrice = newPrice;
  // Preisstufen werden automatisch über den getter neu berechnet
};

/**
 * Gibt die verfügbaren Durchmesser zurück (600-2000mm)
 */
export const getCircularDiameterSteps = (): number[] => {
  const { minDiameter, maxDiameter } = SKYVISION_CIRCULAR_CONFIG.priceRules.limits;
  const steps: number[] = [];
  
  // Verfügbare Größen: 600, 700, 800, 1000, 1100, ..., 2000
  for (let d = minDiameter; d <= 800; d += 100) {
    steps.push(d);
  }
  for (let d = 1000; d <= maxDiameter; d += 100) {
    steps.push(d);
  }
  
  return steps;
};

/**
 * Berechnet die Fläche eines kreisförmigen Fensters
 */
export const calculateCircularArea = (diameter: number): number => {
  const radius = diameter / 2;
  return Math.PI * radius * radius / 1000000; // in m²
};

// Exportiere für Backward-Kompatibilität
export const CIRCULAR_BASE_PRICE = SKYVISION_CIRCULAR_CONFIG.basePrice;
export const getProductBasePrice = () => SKYVISION_CIRCULAR_CONFIG.basePrice;
export const getProductName = () => 'SkyVision CIRCULAR';
